import { formatTW, type CompactStyle } from '@/lib/market/formatters';

/**
 * =============================================================================
 *  registry 驅動的數值呈現（ARCHITECTURE §10.8）。
 *
 *  `compact_style` / `precision` / `unit` 都是 meta.metric_registry 的欄位。
 *  這個模組唯一的工作是把那三個欄位變成一個字串——
 *  **它沒有、也不准有任何一行 `if (metricKey === ...)`。**
 *  一個新的 metric 上線時不需要動到這裡，這就是 P3 驗收 #1 要量的那件事。
 *
 *  資料庫的 compact_style 定義域是 ('none','tw_count','tw_money','si','pct')，
 *  而 P0 的 formatTW() 收的是 ('tw_money','tw_count','si','plain')。
 *  這裡是那兩個定義域唯一的轉接點；P0 的 formatters.ts 不動。
 * ========================================================================== */

export type RegistryCompactStyle = 'none' | 'tw_count' | 'tw_money' | 'si' | 'pct';

export interface Formattable {
  compact_style: string;
  precision: number;
  unit?: string | null | undefined;
}

const TO_P0_STYLE: Record<RegistryCompactStyle, CompactStyle> = {
  none: 'plain',
  tw_count: 'tw_count',
  tw_money: 'tw_money',
  si: 'si',
  pct: 'plain',
};

export function formatMetricValue(value: number | null | undefined, m: Formattable): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const style = (TO_P0_STYLE[m.compact_style as RegistryCompactStyle] ?? 'plain') satisfies CompactStyle;
  return formatTW(value, style, m.precision);
}

/** 值 + 單位。單位是 registry 欄位，不是寫在元件裡的字串。 */
export function formatMetricWithUnit(value: number | null | undefined, m: Formattable): string {
  const body = formatMetricValue(value, m);
  if (body === '—') return body;
  const unit = m.unit ?? '';
  if (unit === '') return body;
  // 「%」「倍」貼著數字，其餘（元、張）也貼著——中文金融介面不留空格。
  return `${body}${unit}`;
}

/** 價格：市場決定小數位慣例（台股 2 位、美股 2 位），但仍走同一條 tabular-nums 路徑。 */
export function formatPrice(value: number | null | undefined, precision = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
}

/** 大整數（成交量）：千分位，不縮寫——成交量看的就是位數。 */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.round(value).toLocaleString('en-US');
}
